import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tooltip,
  TextField,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useDropzone } from 'react-dropzone';
import DialogTransition from './Common/DialogTransition';
import SwalToast from './Common/SwalToast';
import useCustomTheme from '../hooks/useCustomTheme';

const PRODUCT_ICON_LOGO = 'Product Icon';
const BROWSER_ICON_LOGO = 'Browser Icon';
const CLIENT_ICON_LOGO = 'Client Icon';

const LIGHT_THEME_NAME = 'light';
const DARK_THEME_NAME = 'dark';

const imgStyle = {
  width: '250px',
  height: '140px',
  objectFit: 'contain',
};

const AddIconDialog = ({
  open = false,
  handleClose,
  iconType,
  productName,
  setProductName,
  clientName,
  orgName,
  tabIndex,
  fetchThemeConfiguration,
  setBrowserTabLogo,
  setProductLogo,
  setClientLogo,
}) => {
  const theme = useTheme();
  const { saveTheme } = useCustomTheme();
  const [logo, setLogo] = useState(undefined);
  const [isDimensionCorrect, setIsDimensionCorrect] = useState(true);

  const isUploadEnabled = useMemo(() => {
    if (iconType === BROWSER_ICON_LOGO) {
      return true;
    }
    return !!logo;
  }, [iconType, logo]);

  const handleProductNameChange = useCallback(
    (e) => {
      setProductName(e.target.value);
    },
    [setProductName]
  );

  const handleReset = useCallback(() => {
    setLogo(undefined);
    setIsDimensionCorrect(true);
    if (iconType === BROWSER_ICON_LOGO) {
      setProductName('');
    }
  }, [iconType, setProductName]);

  const onProductLogoChange = useCallback(() => {
    const themeName = tabIndex === 0 ? LIGHT_THEME_NAME : DARK_THEME_NAME;

    const handleSuccess = (successMessage) => {
      handleClose();
      SwalToast({
        icon: 'success',
        title: successMessage,
      });
      fetchThemeConfiguration(orgName, clientName, themeName).then((res) => {
        if (res?.data) {
          setProductName(res.data?.product_name);
          setBrowserTabLogo(res.data?.browser_icon);
          setProductLogo(res.data?.product_icon);
          setClientLogo(res.data?.client_icon);
        }
      });
    };

    switch (iconType) {
      case PRODUCT_ICON_LOGO:
        if (!logo || !isDimensionCorrect) return;
        saveTheme(orgName, {
          product: clientName,
          product_icon: logo,
          theme: themeName,
        }).then(() => handleSuccess('Product Logo is uploaded successfully.'));
        break;

      case BROWSER_ICON_LOGO:
        saveTheme(orgName, {
          browser_icon: logo,
          product_name: productName,
          product: clientName,
          theme: themeName,
        }).then(() => handleSuccess('Browser Logo is uploaded successfully.'));
        break;

      case CLIENT_ICON_LOGO:
        if (!logo || !isDimensionCorrect) return;
        saveTheme(orgName, {
          client_icon: logo,
          product: clientName,
          theme: themeName,
        }).then(() => handleSuccess('Client Logo is uploaded successfully.'));
        break;

      default:
        break;
    }
  }, [
    tabIndex,
    handleClose,
    fetchThemeConfiguration,
    orgName,
    clientName,
    setProductName,
    setBrowserTabLogo,
    setProductLogo,
    setClientLogo,
    iconType,
    isDimensionCorrect,
    saveTheme,
    logo,
    productName,
  ]);

  const handleOnDropChange = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        SwalToast({
          icon: 'error',
          title:
            'Unsupported file format. Please upload PNG, SVG or JPEG only.',
        });
        return;
      }

      if (acceptedFiles.length === 0) return;

      if (iconType === PRODUCT_ICON_LOGO || iconType === CLIENT_ICON_LOGO) {
        const newReader = new FileReader();
        newReader.readAsDataURL(acceptedFiles[0]);
        newReader.onload = () => {
          const { result } = newReader;

          const img = new Image();
          img.src = result;
          img.onload = () => {
            if (Math.floor(img.width / img.height) !== 2) {
              SwalToast({
                icon: 'error',
                title:
                  'The uploaded image does not meet the required aspect ratio (2:1).',
              });
              setIsDimensionCorrect(false);
            } else {
              setIsDimensionCorrect(true);
            }
          };
        };
      }

      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const svgData = reader.result;
          switch (iconType) {
            case PRODUCT_ICON_LOGO:
            case BROWSER_ICON_LOGO:
            case CLIENT_ICON_LOGO:
              setLogo(svgData);
              break;
            default:
              break;
          }
        };
      });
    },
    [iconType]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/svg+xml': ['.svg'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    maxFiles: 1,
    onDrop: handleOnDropChange,
  });

  return (
    <Dialog
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={true}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>Upload {iconType}</DialogTitle>
      <IconButton
        onClick={handleClose}
        size="large"
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon style={{ color: theme.palette.other.icon }} />
      </IconButton>
      <DialogContent dividers>
        <Grid container justifyContent="center" textAlign="center">
          <Grid
            item
            {...getRootProps({ className: 'dropzone' })}
            id="dm_di_import_dts_all_type_fa_drop_box"
            sx={{
              border: `1px dashed ${theme.palette.primary.main} !important `,
              pt: '27px !important',
              borderRadius: '4px',
            }}
            width="80%"
            height={200}
          >
            <Grid container alignItems="center" justifyContent="center">
              {logo === undefined ? (
                <Box>
                  <UploadFileIcon
                    sx={{
                      width: '60px',
                      height: '60px',
                      color: 'primary.main',
                    }}
                  />
                  <br />
                  <br />
                  <input {...getInputProps()} hidden />
                  <Grid display="flex" flexDirection="row">
                    <Grid mr={1}>
                      <Typography variant="body2">
                        Drag and drop or choose a file to upload
                      </Typography>
                    </Grid>
                    <Grid mt={0.3}>
                      <Tooltip
                        title={
                          <Typography variant="body4">
                            Only SVG, PNG and JPEG allowed.
                          </Typography>
                        }
                        placement="right-end"
                        arrow
                      >
                        <HelpOutlineIcon
                          sx={{
                            height: '14px',
                            width: '14px',
                            color: 'primary.main',
                          }}
                        />
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Grid
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <img src={logo} alt="Logo" style={imgStyle} />
                </Grid>
              )}
            </Grid>
          </Grid>
          {iconType === BROWSER_ICON_LOGO && (
            <Box width="80%" mt={2}>
              <TextField
                type="text"
                label="Product name"
                placeholder="Enter product name"
                name="productName"
                value={productName}
                onChange={handleProductNameChange}
              />
            </Box>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={onProductLogoChange}
          disabled={!isUploadEnabled}
          title={!isUploadEnabled ? 'Please upload logo to proceed' : ''}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddIconDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  iconType: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  setProductName: PropTypes.func.isRequired,
  clientName: PropTypes.string.isRequired,
  orgName: PropTypes.string.isRequired,
  tabIndex: PropTypes.number.isRequired,
  fetchThemeConfiguration: PropTypes.func.isRequired,
  setBrowserTabLogo: PropTypes.func.isRequired,
  setProductLogo: PropTypes.func.isRequired,
  setClientLogo: PropTypes.func.isRequired,
};

export default AddIconDialog;
